import { Alert, Button, Divider, Stack, Typography } from "@mui/material";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { AutocompleteModel, GqlUserCreateInput } from "~/__generated__";
import GaiaAutocompleteOptionsDropdown from "~/modules/app/components/Autocomplete/GaiaAutocompleteOptionsDropdown";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

import useCreateUser from "../../api/useUserCreate";
import PhoneNumberField from "../PhoneNumberField";

type Props = {
    onSubmitted: (newId: string) => void;
    newUser?: GqlUserCreateInput;
};

type CreateUserFormData = GqlUserCreateInput & {
    confirmPassword: string;
};

const initialData: GqlUserCreateInput = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    roleId: "",
};

export default function CreateUserForm({ newUser = initialData, onSubmitted }: Props) {
    const { t } = useTranslation("user");
    const { createUser, error } = useCreateUser();

    const formMethods = useForm<CreateUserFormData>({
        defaultValues: {
            ...newUser,
            confirmPassword: "",
        },
    });

    const {
        control,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting, isLoading, isDirty, isSubmitted },
    } = formMethods;

    const onSubmit: SubmitHandler<CreateUserFormData> = async ({
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        confirmPassword,
        ...userData
    }) => {
        const result = await createUser(userData);
        const newId = result.data?.userCreate.id;
        onSubmitted(newId!);
    };

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <GaiaConfirmCloseDialog blocker={blocker} />
                <Stack gap={2}>
                    {errors.root && (
                        <Alert severity="error">
                            <Typography>{errors.root.message}</Typography>
                        </Alert>
                    )}

                    <TextFieldElement name="firstName" label={t("userCreateForm.labelFirstname")} required />

                    <TextFieldElement name="lastName" label={t("userCreateForm.labelLastname")} required />

                    <Controller
                        name="roleId"
                        control={formMethods.control}
                        render={({ field }) => (
                            <GaiaAutocompleteOptionsDropdown
                                sx={{ mt: 2 }}
                                model={AutocompleteModel.TenantRole}
                                label={t("userCreateForm.labelRole")}
                                error={!!errors.roleId}
                                helperText={errors.roleId?.message}
                                {...field}
                            />
                        )}
                    />

                    <Divider>
                        <Typography variant="caption" textTransform="uppercase">
                            {t("userCreateForm.sectionContact")}
                        </Typography>
                    </Divider>

                    <PhoneNumberField<CreateUserFormData> control={control} name="phone" required />

                    <TextFieldElement name="email" label={t("userCreateForm.labelEmail")} required />

                    <Divider>
                        <Typography variant="caption" textTransform="uppercase">
                            {t("userCreateForm.sectionPassword")}
                        </Typography>
                    </Divider>

                    <TextFieldElement
                        name="password"
                        type="password"
                        label={t("userCreateForm.labelPassword")}
                        required
                    />

                    <TextFieldElement
                        name="confirmPassword"
                        type="password"
                        label={t("userCreateForm.labelConfirmPassword")}
                        rules={{
                            validate: value =>
                                value === formMethods.getValues("password") ||
                                t("userCreateForm.errorConfirmPasswordMismatch"),
                        }}
                        required
                    />

                    <Button
                        sx={{ mt: 3 }}
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={!isDirty}
                        loading={isLoading || isSubmitting}
                    >
                        {t("userCreateForm.btnSubmit")}
                    </Button>
                </Stack>
            </form>
        </FormProvider>
    );
}
